/*
 * Configurator tracking.
 *
 * The configurator runs in modals with no URL change, so analytics sees one
 * page view for the whole journey. This sends the steps to GA4 so one
 * visitor's path through the configurator can be followed.
 *
 * Measurement starts when the configurator is opened and on screen, and ends
 * when it is closed. Events, in order:
 *
 *   configurator_open          it opened and is visible
 *   vehicle_selected           a truck was committed
 *   configurator_customising   the customising screen became visible
 *   configurator_add_to_cart   the configurator's own cart request succeeded
 *   configurator_close         it was closed
 *
 * Four rules, agreed 17 September 2026 as the ones worth keeping:
 *
 *   1. A milestone is sent only when it really happened. A step sitting in the
 *      page markup is not the same as a step on screen.
 *   2. A milestone is never repeated because the page shuffled.
 *   3. The add to cart is read from the configurator's own successful cart
 *      request, never from a click and never from another cart add.
 *   4. Nothing is sent unless Shopify says analytics is allowed, checked
 *      before each event.
 *
 * GA4 supplies the visitor and session identity, so this file does not track
 * people. It observes only: it never blocks a click, alters a request or a
 * response, or throws into the configurator's own code.
 */
(function () {
  'use strict';

  var GA4_ID = 'G-14V7JRW0Q2';
  var MARKERS = ['_mmy', '_customizer_image'];
  var KEY_JOURNEY = 'mv_cfg_journey';

  var openModal = null; // the modal we are currently measuring
  var sent = {}; // milestones already sent for this opening
  var lastSource = null;

  /* Choosing a truck reloads the page, so the journey carries across it. Two
   * things only, for this tab: an anonymous reference and the fact that the
   * opening was already counted. No history, nothing about the person, and
   * nothing that outlives the tab. */
  function journey() {
    try {
      var raw = sessionStorage.getItem(KEY_JOURNEY);
      var j = raw ? JSON.parse(raw) : null;
      if (j && j.id) return j;
    } catch (e) {}
    return null;
  }

  function startJourney() {
    var existing = journey();
    if (existing) return existing;
    var fresh = { id: Math.random().toString(36).slice(2, 10), openCounted: false };
    try {
      sessionStorage.setItem(KEY_JOURNEY, JSON.stringify(fresh));
    } catch (e) {}
    return fresh;
  }

  function markOpenCounted(j) {
    if (!j || j.openCounted) return;
    j.openCounted = true;
    try {
      sessionStorage.setItem(KEY_JOURNEY, JSON.stringify(j));
    } catch (e) {}
  }

  function endJourney() {
    try {
      sessionStorage.removeItem(KEY_JOURNEY);
    } catch (e) {}
  }

  function log(what, detail) {
    try {
      console.error('[configurator-tracking] ' + what, detail || '');
    } catch (e) {}
  }

  /* Rule 1: on screen, not merely present. Ancestors count. */
  function shown(el) {
    if (!el || !el.isConnected) return false;
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.classList && node.classList.contains('hidden')) return false;
      if (node.hasAttribute && node.hasAttribute('hidden')) return false;
      var s = window.getComputedStyle(node);
      if (!s) return false;
      if (s.display === 'none' || s.visibility === 'hidden') return false;
      if (parseFloat(s.opacity) === 0) return false;
      node = node.parentElement;
    }
    return el.getClientRects().length > 0;
  }

  function visibleStep(modal, tag) {
    if (!modal || !shown(modal)) return null;
    var steps = modal.querySelectorAll(tag);
    for (var i = 0; i < steps.length; i++) {
      if (shown(steps[i])) return steps[i];
    }
    return null;
  }

  function stepNumber(modal) {
    if (visibleStep(modal, 'customizer-step3')) return 3;
    if (visibleStep(modal, 'customizer-step2')) return 2;
    if (visibleStep(modal, 'customizer-step1')) return 1;
    return 0;
  }

  /* Rule 4: permission, from Shopify's own API, before each event. */
  var privacyAsked = false;

  function askPrivacy() {
    if (privacyAsked) return;
    privacyAsked = true;
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (error) {
            if (error) log('privacy api failed to load', error);
          }
        );
      }
    } catch (e) {
      log('privacy api request threw', e);
    }
  }

  function allowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;
      return cp.analyticsProcessingAllowed() === true;
    } catch (e) {
      return false;
    }
  }

  function vehicle() {
    try {
      var v = JSON.parse(localStorage.getItem('vehicle'));
      if (v && v.make) {
        return {
          sku: v.sku || '',
          name: [v.make, v.model, v.year].filter(Boolean).join(' '),
        };
      }
    } catch (e) {}
    return { sku: '', name: '' };
  }

  function product() {
    try {
      var p = JSON.parse(localStorage.getItem('product'));
      if (p) {
        return {
          sku: (p.sku || '').toString(),
          name: (p.detail && p.detail.title) || p.title || '',
        };
      }
    } catch (e) {}
    return { sku: '', name: '' };
  }

  function gtagFn() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    return window.gtag;
  }

  /* Rules 2 and 4: once per opening, and only with permission. */
  function send(name, extra) {
    if (!openModal) return;
    if (sent[name]) return;
    if (!allowed()) return;

    var v = vehicle();
    var p = product();
    var j = journey();
    var payload = {
      send_to: GA4_ID,
      open_method: openModal.mvMethod,
      entry_step: openModal.mvEntryStep,
    };
    if (j) payload.journey_id = j.id;
    if (v.name) {
      payload.vehicle_sku = v.sku;
      payload.vehicle_name = v.name;
    }
    if (p.sku) {
      payload.product_sku = p.sku;
      payload.product_name = p.name;
    }
    for (var k in extra || {}) {
      if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k];
    }

    try {
      gtagFn()('event', name, payload);
      sent[name] = true;
    } catch (e) {
      log('send failed for ' + name, e);
    }
  }

  function openMethod(source) {
    try {
      if (new URLSearchParams(window.location.search).get('view-detail') === '1') {
        return 'auto_detail';
      }
    } catch (e) {}
    return source ? 'button' : 'scroll_prompt';
  }

  function beginMeasuring(modal) {
    if (!shown(modal)) return;
    var step = stepNumber(modal);
    if (!step) return; // nothing on screen yet, try again on the next change

    openModal = modal;
    openModal.mvMethod = openMethod(lastSource);
    openModal.mvEntryStep = step;
    sent = {};

    var j = startJourney();
    if (j.openCounted) {
      // The same journey, resumed after the reload that follows choosing a
      // truck. It opened once, so it is not counted as opening again.
      sent.configurator_open = true;
    } else {
      send('configurator_open');
      if (sent.configurator_open) markOpenCounted(j);
    }
    if (step === 3) send('configurator_customising');
  }

  function stopMeasuring(why) {
    if (!openModal) return;
    send('configurator_close', { close_reason: why || 'closed' });
    openModal = null;
    sent = {};
    // Leaving the page for the truck reload keeps the journey; closing ends it.
    if (why !== 'reloading') endJourney();
  }

  /* Watch for the steps changing, including one already in the page being
   * revealed, which is why attributes are watched as well as contents. */
  function watch() {
    var observer = new MutationObserver(function () {
      try {
        if (!openModal) {
          if (pendingModal && shown(pendingModal)) beginMeasuring(pendingModal);
          return;
        }
        if (!shown(openModal)) {
          stopMeasuring('hidden');
          return;
        }
        if (visibleStep(openModal, 'customizer-step3')) send('configurator_customising');
      } catch (e) {
        log('watch', e);
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  }

  var pendingModal = null;
  var vehicleReloadExpected = false;

  /* The modal announces itself on its own element and the announcement does
   * not travel, so this listens to the announcement itself. */
  function interceptDispatch() {
    if (EventTarget.prototype.dispatchEvent.__mvWrapped) return;
    var original = EventTarget.prototype.dispatchEvent;
    var wrapped = function (event) {
      var result = original.apply(this, arguments);
      try {
        if (!event || !this || !this.tagName) return result;
        if (this.tagName.toLowerCase() !== 'customizer-modal') return result;
        if (event.__mvSeen) return result;
        event.__mvSeen = true;

        if (event.type === 'modal:opened') {
          pendingModal = this;
          beginMeasuring(this);
        } else if (event.type === 'modal:closed') {
          pendingModal = null;
          stopMeasuring('closed');
        }
      } catch (e) {
        log('modal event', e);
      }
      return result;
    };
    wrapped.__mvWrapped = true;
    EventTarget.prototype.dispatchEvent = wrapped;
  }

  /* Rule 3: the configurator's own successful cart request, nothing else. */
  function cartAddPath() {
    var root = '/';
    try {
      root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (e) {}
    try {
      return new URL(root + 'cart/add.js', window.location.href).pathname;
    } catch (e) {
      return '/cart/add.js';
    }
  }

  function isCartAdd(url, method) {
    try {
      if (String(method || 'GET').toUpperCase() !== 'POST') return false;
      var resolved = new URL(String(url), window.location.href);
      if (resolved.origin !== window.location.origin) return false;
      return resolved.pathname === cartAddPath();
    } catch (e) {
      return false;
    }
  }

  function isMarkerName(key) {
    var name = String(key);
    var bracket = name.match(/\[([^\]]+)\]/);
    if (bracket) name = bracket[1];
    for (var i = 0; i < MARKERS.length; i++) {
      if (name === MARKERS[i]) return true;
    }
    return false;
  }

  function objectHasMarker(value) {
    if (!value || typeof value !== 'object') return false;
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        if (objectHasMarker(value[i])) return true;
      }
      return false;
    }
    for (var k in value) {
      if (!Object.prototype.hasOwnProperty.call(value, k)) continue;
      if (isMarkerName(k)) return true;
      if (objectHasMarker(value[k])) return true;
    }
    return false;
  }

  // Marker must be a field name, not text inside a value.
  function isOurCart(body) {
    if (typeof body !== 'string' || !body) return false;
    try {
      if (objectHasMarker(JSON.parse(body))) return true;
    } catch (e) {}
    try {
      var found = false;
      new URLSearchParams(body).forEach(function (_v, key) {
        if (isMarkerName(key)) found = true;
      });
      return found;
    } catch (e) {}
    return false;
  }

  function wrapFetch() {
    if (!window.fetch || window.fetch.__mvWrapped) return;
    var original = window.fetch;
    var wrapped = function (input, init) {
      var mine = false;
      try {
        var isRequest = typeof Request !== 'undefined' && input instanceof Request;
        var url = isRequest ? input.url : input;
        var method = (init && init.method) || (isRequest && input.method) || 'GET';
        var body = init && typeof init.body === 'string' ? init.body : null;
        mine = isCartAdd(url, method) && isOurCart(body);
      } catch (e) {}

      var result = original.apply(this, arguments);
      if (!mine) return result;
      try {
        result.then(
          function (response) {
            try {
              if (response && response.ok) send('configurator_add_to_cart');
            } catch (e) {}
          },
          function () {}
        );
      } catch (e) {}
      return result;
    };
    wrapped.__mvWrapped = true;
    window.fetch = wrapped;
  }

  /* Bootstrap. Interception first, so nothing is missed. */
  interceptDispatch();
  wrapFetch();
  askPrivacy();

  function setup() {
    document.addEventListener(
      'click',
      function (e) {
        try {
          lastSource = e.target.closest
            ? e.target.closest('[data-toggle="modal"], .btn-primary-header')
            : null;
        } catch (err) {}
      },
      true
    );

    // The truck choice reloads the page off a product page, so this is sent
    // as the theme announces it, before the reload takes the page away.
    document.addEventListener('vehicle:changed', function () {
      try {
        send('vehicle_selected');
        vehicleReloadExpected = true;
      } catch (e) {}
    });

    window.addEventListener('pagehide', function () {
      try {
        stopMeasuring(vehicleReloadExpected ? 'reloading' : 'left_page');
      } catch (e) {}
    });

    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
